import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSessionState } from "@/hooks/useSessionState";
import {
  Plus,
  Search,
  Users,
  Target,
  Eye,
  Edit,
  Trash2,
  RefreshCw,
  Filter,
  Calendar,
  TrendingUp,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { useDebounce } from "../hooks/useDebounce";
import AdminLayout from "@/components/layout/AdminLayout";
import {
  fetchUserGroups as apiFetchUserGroups,
  toggleUserGroupStatus,
  deleteUserGroup,
  refreshGroupMemberCount,
  refreshAllGroupMemberCounts,
  getTotalUniqueMembers,
  UserGroup,
} from "../api/user-groups";

export default function UserGroupsList() {
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useSessionState("userGroupsList.searchTerm", "");
  const [filterStatus, setFilterStatus] = useSessionState("userGroupsList.filterStatus", "all");
  const [refreshing, setRefreshing] = useState(false);
  const [totalUniqueMembers, setTotalUniqueMembers] = useState<number>(0);
  const { toast } = useToast();

  // Debounce search term with 500ms delay
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  useEffect(() => {
    fetchUserGroups();
    fetchTotalUniqueMembers();
  }, []);

  useEffect(() => {
    fetchUserGroups();
  }, [debouncedSearchTerm, filterStatus]);

  const fetchUserGroups = async () => {
    try {
      setLoading(true);
      const response = await apiFetchUserGroups(
        { page: 1, pageSize: 100 }, // Get all groups for now
        {
          search: debouncedSearchTerm,
          status: filterStatus === "all" ? undefined : filterStatus,
        }
      );
      setUserGroups(response.data);
    } catch (error) {
      console.error("Error fetching user groups:", error);
      toast({
        title: "Error",
        description:
          "Failed to fetch user groups. The database tables may not exist yet.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchTotalUniqueMembers = async () => {
    try {
      const total = await getTotalUniqueMembers();
      setTotalUniqueMembers(total);
    } catch (error) {
      console.error("Error fetching total unique members:", error);
      // Don't show toast for this error as it's not critical
    }
  };

  const refreshGroupCounts = async () => {
    try {
      setRefreshing(true);

      // Use the new function to refresh all groups at once
      const result = await refreshAllGroupMemberCounts();

      if (result.success) {
        toast({
          title: "Success",
          description: `Refreshed member counts for ${result.updated} groups successfully.`,
        });

        // Refresh the groups list to show updated counts
        await fetchUserGroups();
        await fetchTotalUniqueMembers();
      } else {
        toast({
          title: "Partial Success",
          description: `Refreshed ${result.updated} groups, but encountered ${result.errors.length} errors.`,
          variant: "destructive",
        });

        // Still refresh the list to show what was updated
        await fetchUserGroups();
        await fetchTotalUniqueMembers();
      }
    } catch (error) {
      console.error("Error refreshing group counts:", error);
      toast({
        title: "Error",
        description: "Failed to refresh group member counts.",
        variant: "destructive",
      });
    } finally {
      setRefreshing(false);
    }
  };

  const toggleGroupStatus = async (groupId: number, isActive: boolean) => {
    try {
      await toggleUserGroupStatus(groupId, isActive);

      toast({
        title: "Success",
        description: `Group ${
          isActive ? "activated" : "deactivated"
        } successfully.`,
      });

      // Refresh the groups list
      await fetchUserGroups();
    } catch (error) {
      console.error("Error toggling group status:", error);
      toast({
        title: "Error",
        description: "Failed to update group status.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteGroup = async (groupId: number) => {
    if (
      !confirm(
        "Are you sure you want to delete this user group? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      await deleteUserGroup(groupId);

      toast({
        title: "Success",
        description: "User group deleted successfully.",
      });

      // Refresh the groups list
      await fetchUserGroups();
    } catch (error) {
      console.error("Error deleting user group:", error);
      toast({
        title: "Error",
        description: "Failed to delete user group.",
        variant: "destructive",
      });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getMemberCountColor = (count: number) => {
    if (count === 0)
      return "text-gray-500 bg-gray-100 dark:bg-gray-800 dark:text-gray-400";
    if (count < 5)
      return "text-blue-600 bg-blue-100 dark:bg-blue-900 dark:text-blue-200";
    if (count < 15)
      return "text-green-600 bg-green-100 dark:bg-green-900 dark:text-green-200";
    if (count < 30)
      return "text-orange-600 bg-orange-100 dark:bg-orange-900 dark:text-orange-200";
    return "text-purple-600 bg-purple-100 dark:bg-purple-900 dark:text-purple-200";
  };

  const getMemberCountBadgeVariant = (count: number) => {
    if (count === 0) return "secondary";
    if (count < 5) return "default";
    if (count < 15) return "default";
    if (count < 30) return "default";
    return "default";
  };

  const getGroupCategory = (groupName: string): string => {
    const categories = {
      "Content Consumption": [
        "Active Book Readers",
        "Audio Content Consumers",
        "Multi-Format Consumers",
        "Content Completion Champions",
      ],
      "Engagement & Interaction": [
        "Engaged Highlighters",
        "Feedback Providers",
        "Category Enthusiasts",
        "Content Discovery Users",
      ],
      "Subscription & Monetization": [
        "Subscription Holders",
        "Free Content Users",
        "Free Content Users with 30+ Minutes Reading",
        "Free Users - No Plan Ever",
        "Expired Subscribers",
        "Cancelled Subscribers",
        "Free Users with Account Age 30+ Days",
        "Free Users with Account Age 90+ Days",
        "Free Users with 5+ Books Read",
        "Free Users with 60+ Minutes Reading",
        "Free Users with 10+ Books Read",
        "Free Users with 2+ Hours Reading",
        "Expired Subscribers with 30+ Days Account Age",
        "Cancelled Subscribers with Reading Activity",
        "Free Users - High Engagement",
        "High-Value Subscribers",
        "At-Risk Churn Users",
      ],
      "User Lifecycle": [
        "Recent Registrations",
        "Inactive Users",
        "Engagement Leaders",
      ],
      "Demographic & Behavioral": [
        "Age Group Specific",
        "Referral Users",
        "Platform Specific Users",
      ],
      "Feature Adoption": ["Feature Adopters", "High Completion Rate Users"],
    };

    for (const [category, groups] of Object.entries(categories)) {
      if (groups.includes(groupName)) {
        return category;
      }
    }
    return "Other";
  };

  const getCategoryColor = (category: string): string => {
    const colors = {
      "Content Consumption":
        "text-blue-600 bg-blue-50 dark:bg-blue-950 dark:text-blue-300",
      "Engagement & Interaction":
        "text-green-600 bg-green-50 dark:bg-green-950 dark:text-green-300",
      "Subscription & Monetization":
        "text-purple-600 bg-purple-50 dark:bg-purple-950 dark:text-purple-300",
      "User Lifecycle":
        "text-orange-600 bg-orange-50 dark:bg-orange-950 dark:text-orange-300",
      "Demographic & Behavioral":
        "text-indigo-600 bg-indigo-50 dark:bg-indigo-950 dark:text-indigo-300",
      "Feature Adoption":
        "text-pink-600 bg-pink-50 dark:bg-pink-950 dark:text-pink-300",
      Other: "text-gray-600 bg-gray-50 dark:bg-gray-950 dark:text-gray-300",
    };
    return colors[category] || colors["Other"];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100"></div>
      </div>
    );
  }

  const actionButtons = (
    <div className="flex flex-wrap gap-2">
      <Button
        onClick={refreshGroupCounts}
        variant="outline"
        disabled={refreshing}
        className="flex items-center gap-2"
      >
        <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
        {refreshing ? "Refreshing..." : "Refresh All Counts"}
      </Button>
      <Button asChild className="flex items-center gap-2">
        <Link to="/admin/user-groups/new">
          <Plus className="h-4 w-4" />
          Create Group
        </Link>
      </Button>
    </div>
  );

  const activeGroups = userGroups.filter((g) => g.is_active).length;
  const groupsWithMembers = userGroups.filter((g) => g.member_count > 0).length;
  const totalMembers = userGroups.reduce(
    (sum, group) => sum + group.member_count,
    0
  );

  return (
    <AdminLayout title="User Groups" actionButton={actionButtons}>
      <div className="space-y-6">
        <div className="text-gray-600 dark:text-gray-400">
          Dynamic user segments for targeted marketing campaigns, push
          notifications, and promotions. Member counts are calculated based on
          actual user behavior and engagement patterns.
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Total Groups
                  </p>
                  <p className="text-2xl font-bold">{userGroups.length}</p>
                </div>
                <Target className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Active Groups
                  </p>
                  <p className="text-2xl font-bold">{activeGroups}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Unique Users
                  </p>
                  <p className="text-2xl font-bold">
                    {totalUniqueMembers.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Users in at least one group
                  </p>
                </div>
                <Users className="h-8 w-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Total Group Members
                  </p>
                  <p className="text-2xl font-bold">
                    {totalMembers.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {groupsWithMembers} groups have members
                  </p>
                </div>
                <Calendar className="h-8 w-8 text-orange-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 h-4 w-4" />
            <Input
              placeholder="Search groups by name or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Groups</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* User Groups */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {userGroups.map((group) => {
            const category = getGroupCategory(group.name);
            const categoryColor = getCategoryColor(category);

            return (
              <Card
                key={group.id}
                className="hover:shadow-lg transition-shadow"
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-semibold line-clamp-1">
                      {group.name}
                    </CardTitle>
                    <Badge
                      variant={group.is_active ? "default" : "secondary"}
                      className={
                        group.is_active
                          ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                          : ""
                      }
                    >
                      {group.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                  <CardDescription className="line-clamp-2">
                    {group.description || "No description provided"}
                  </CardDescription>
                  <Badge
                    variant="outline"
                    className={`text-xs ${categoryColor}`}
                  >
                    {category}
                  </Badge>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {/* Members Count */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Members
                      </span>
                      <Badge
                        variant={getMemberCountBadgeVariant(group.member_count)}
                        className={`flex items-center gap-1 ${getMemberCountColor(
                          group.member_count
                        )}`}
                      >
                        <Users className="h-3 w-3" />
                        {group.member_count.toLocaleString()}
                      </Badge>
                    </div>

                    {/* Last Refreshed */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Last Refreshed
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {group.last_refreshed_at
                          ? formatDate(group.last_refreshed_at)
                          : "Never"}
                      </span>
                    </div>

                    {/* Created */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Created
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDate(group.created_at)}
                      </span>
                    </div>

                    {/* Criteria Preview */}
                    {group.criteria &&
                      Object.keys(group.criteria).length > 0 && (
                        <div className="pt-2 border-t">
                          <span className="text-sm text-gray-600 dark:text-gray-400 block mb-1">
                            Targeting Criteria:
                          </span>
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(group.criteria)
                              .slice(0, 3)
                              .map(([key, value]) => (
                                <Badge
                                  key={key}
                                  variant="outline"
                                  className="text-xs"
                                >
                                  {key}: {String(value)}
                                </Badge>
                              ))}
                            {Object.keys(group.criteria).length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{Object.keys(group.criteria).length - 3} more
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}

                    {/* Actions */}
                    <div className="flex items-center justify-between pt-2 border-t">
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          asChild
                          className="h-8 px-2"
                        >
                          <Link to={`/admin/user-groups/${group.id}`}>
                            <Eye className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          asChild
                          className="h-8 px-2"
                        >
                          <Link to={`/admin/user-groups/${group.id}/edit`}>
                            <Edit className="h-4 w-4" />
                          </Link>
                        </Button>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            toggleGroupStatus(group.id, !group.is_active)
                          }
                          className="h-8 px-2"
                        >
                          {group.is_active ? (
                            <AlertCircle className="h-4 w-4 text-orange-500" />
                          ) : (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteGroup(group.id)}
                          className="h-8 px-2 text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {userGroups.length === 0 && !loading && (
          <div className="text-center py-12">
            <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              No user groups found
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {searchTerm || filterStatus !== "all"
                ? "Try adjusting your search or filter criteria."
                : "Get started by creating your first user group."}
            </p>
            {!searchTerm && filterStatus === "all" && (
              <Button asChild>
                <Link to="/admin/user-groups/new">
                  <Plus className="h-4 w-4 mr-2" />
                  Create First Group
                </Link>
              </Button>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
